import { useState, useMemo, useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { TransmittersPage } from '@/features/operations-transmitters/pages/TransmittersPage';
import type { Transmitter } from '@/features/operations-transmitters/types';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import { useCanViewTailscaleMesh } from '@/hooks/useCanViewTailscaleMesh';
import { useShootback, useToggleShootback } from '../hooks/useShootback';
import { useTailscaleGateways } from '../hooks/useTailscaleGateways';
import { createGatewayColumns } from '../columns';

export default function GatewaysPage() {
  const [currentTransmitters, setCurrentTransmitters] = useState<Transmitter[]>([]);
  const [lightbox, setLightbox] = useState<{ url: string; name: string } | null>(null);
  const isSuperAdmin = useIsSuperAdmin();
  const canViewMesh = useCanViewTailscaleMesh();
  const canSeeRemoteAccess = isSuperAdmin && canViewMesh;

  const macs = useMemo(
    () => currentTransmitters.map((t: Transmitter) => t.dev_id).filter(Boolean) as string[],
    [currentTransmitters],
  );

  const shootbackQuery = useShootback(macs);
  const toggleShootback = useToggleShootback();
  const tailscaleQuery = useTailscaleGateways(canSeeRemoteAccess);

  const handleToggle = useCallback(
    (mac: string, enabled: boolean) => {
      toggleShootback.mutate({ mac, enabled });
    },
    [toggleShootback.mutate],
  );

  const handleImageClick = useCallback(
    (url: string, name: string) => setLightbox({ url, name }),
    [],
  );

  const columns = useMemo(
    () => createGatewayColumns(
      shootbackQuery.data || [],
      canSeeRemoteAccess
        ? {
            gateways: tailscaleQuery.data ?? [],
            status: tailscaleQuery.data ? 'ready' : tailscaleQuery.isError ? 'error' : 'loading',
          }
        : null,
      handleToggle,
      handleImageClick,
    ),
    [shootbackQuery.data, tailscaleQuery.data, tailscaleQuery.isError, canSeeRemoteAccess, handleToggle, handleImageClick],
  );

  return (
    <>
      <TransmittersPage
        title="Gateways"
        subtitle="IoT gateway management"
        storageKeyPrefix="atek-gateways"
        apiParams={{ type: 'gateways' }}
        columns={columns}
        exportFilename="gateways"
        emptyMessage="No gateways found"
        deviceType="gateways"
        onTransmittersChange={setCurrentTransmitters}
      />

      {/* Image Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(open) => { if (!open) setLightbox(null); }}>
        <DialogContent className="max-w-3xl p-2">
          {lightbox && (
            <img
              src={lightbox.url}
              alt={lightbox.name}
              className="w-full h-auto max-h-[85vh] object-contain rounded"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
